import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { CopyrightTransfer, OwnershipHistory } from '../../types/blockchain';
import { 
  ArrowRightIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  UserIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const CopyrightTransferManager: React.FC = () => {
  const { wallet } = useWeb3();
  const [transfers, setTransfers] = useState<CopyrightTransfer[]>([]);
  const [ownershipHistory, setOwnershipHistory] = useState<OwnershipHistory[]>([]);
  const [selectedTransfer, setSelectedTransfer] = useState<CopyrightTransfer | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // 转让表单数据
  const [transferForm, setTransferForm] = useState({
    workId: '',
    workTitle: '',
    toOwner: '',
    toOwnerName: '',
    transferPrice: '',
    currency: 'SOL' as 'SOL' | 'USDC' | 'IBOM',
    transferType: 'full' as 'full' | 'partial' | 'license',
    transferPercentage: 100,
    terms: '',
    expiryDate: ''
  });

  useEffect(() => {
    if (wallet?.address) {
      loadTransferData();
    }
  }, [wallet]);

  const loadTransferData = async () => {
    setLoading(true);
    try {
      // Mock 版权转让数据
      const mockTransfers: CopyrightTransfer[] = [
        {
          id: 'transfer_1',
          workId: 'work_1',
          workTitle: 'Sunset Dreams',
          fromOwner: wallet?.address || '0x123...',
          fromOwnerName: '原创作者',
          toOwner: '0x456...',
          toOwnerName: '音乐制作公司',
          transferPrice: '5.0',
          currency: 'SOL',
          transferType: 'full',
          transferPercentage: 100,
          contractAddress: '0xabc123...',
          timestamp: Date.now() - 86400000,
          transactionHash: '0xdef456...',
          status: 'completed',
          terms: '完全转让音乐作品《Sunset Dreams》的所有版权，包括发行权、表演权、改编权等。'
        },
        {
          id: 'transfer_2',
          workId: 'work_2',
          workTitle: 'Electric Nights',
          fromOwner: '0x789...',
          fromOwnerName: '独立音乐人',
          toOwner: wallet?.address || '0x123...',
          toOwnerName: '您',
          transferPrice: '2.5',
          currency: 'SOL',
          transferType: 'partial',
          transferPercentage: 50,
          contractAddress: '0xghi789...',
          timestamp: Date.now() - 3600000,
          transactionHash: '0xjkl012...',
          status: 'pending',
          terms: '转让50%版权份额，保留原创者署名权。'
        },
        {
          id: 'transfer_3',
          workId: 'work_3',
          workTitle: 'Ocean Waves',
          fromOwner: wallet?.address || '0x123...',
          fromOwnerName: '您',
          toOwner: '0xabc...',
          toOwnerName: '流媒体平台',
          transferPrice: '1.0',
          currency: 'SOL',
          transferType: 'license',
          transferPercentage: 0,
          contractAddress: '0xmno345...',
          timestamp: Date.now() - 1800000,
          transactionHash: '0xpqr678...',
          status: 'completed',
          terms: '授权3年独家发行权，到期自动终止。',
          expiryDate: Date.now() + 94608000000 // 3年后
        }
      ];

      // Mock 所有权历史
      const mockOwnership: OwnershipHistory[] = [
        {
          workId: 'work_1',
          workTitle: 'Sunset Dreams',
          currentOwner: '0x456...',
          currentOwnerName: '音乐制作公司',
          ownershipPercentage: 100,
          transfers: [mockTransfers[0]],
          createdAt: Date.now() - 172800000
        },
        {
          workId: 'work_2',
          workTitle: 'Electric Nights',
          currentOwner: wallet?.address || '0x123...',
          currentOwnerName: '您',
          ownershipPercentage: 50,
          transfers: [mockTransfers[1]],
          createdAt: Date.now() - 259200000
        }
      ];

      setTransfers(mockTransfers);
      setOwnershipHistory(mockOwnership);
    } catch (error) {
      console.error('Failed to load transfer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransfer = async () => {
    if (!transferForm.workTitle || !transferForm.toOwner || !transferForm.transferPrice) {
      alert('请填写完整的转让信息');
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newTransfer: CopyrightTransfer = {
        id: `transfer_${Date.now()}`,
        workId: `work_${Date.now()}`,
        workTitle: transferForm.workTitle,
        fromOwner: wallet?.address || '0x123...',
        fromOwnerName: '您',
        toOwner: transferForm.toOwner,
        toOwnerName: transferForm.toOwnerName || '未知用户',
        transferPrice: transferForm.transferPrice,
        currency: transferForm.currency,
        transferType: transferForm.transferType,
        transferPercentage: transferForm.transferPercentage,
        contractAddress: `0x${Math.random().toString(16).substring(2, 10)}...`,
        timestamp: Date.now(),
        transactionHash: `0x${Math.random().toString(16).substring(2, 10)}...`,
        status: 'pending',
        terms: transferForm.terms,
        expiryDate: transferForm.expiryDate ? new Date(transferForm.expiryDate).getTime() : undefined
      };

      setTransfers(prev => [newTransfer, ...prev]);
      setShowTransferModal(false);
      setTransferForm({
        workId: '',
        workTitle: '',
        toOwner: '',
        toOwnerName: '',
        transferPrice: '',
        currency: 'SOL' as 'SOL' | 'USDC' | 'IBOM',
        transferType: 'full' as 'full' | 'partial' | 'license',
        transferPercentage: 100,
        terms: '',
        expiryDate: ''
      });
      
      alert('版权转让申请已提交！');
    } catch (error) {
      console.error('Failed to create transfer:', error);
      alert('转让申请失败，请重试');
    }
  };

  const getStatusIcon = (status: CopyrightTransfer['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      case 'cancelled':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'disputed':
        return <ExclamationTriangleIcon className="w-5 h-5 text-orange-500" />;
    }
  };

  const getStatusText = (status: CopyrightTransfer['status']) => {
    switch (status) {
      case 'completed': return '已完成';
      case 'pending': return '处理中';
      case 'cancelled': return '已取消';
      case 'disputed': return '争议中';
    }
  };

  const getTransferTypeText = (type: CopyrightTransfer['transferType']) => {
    switch (type) {
      case 'full': return '完全转让';
      case 'partial': return '部分转让';
      case 'license': return '授权许可';
    }
  };

  if (!wallet?.isConnected) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">请先连接钱包以查看版权转让记录</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">版权转让管理</h1>
        <p className="text-gray-600">管理音乐作品的版权转让和所有权变更</p>
      </div>

      {/* 操作按钮 */}
      <div className="mb-8">
        <button
          onClick={() => setShowTransferModal(true)}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <DocumentTextIcon className="w-5 h-5" />
          发起版权转让
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 转让记录 */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">转让记录</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : transfers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              暂无转让记录
            </div>
          ) : (
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {transfers.map((transfer) => (
                <div 
                  key={transfer.id} 
                  className="p-6 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedTransfer(transfer)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-lg">{transfer.workTitle}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {getTransferTypeText(transfer.transferType)}
                        {transfer.transferType === 'partial' && ` (${transfer.transferPercentage}%)`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(transfer.status)}
                      <span className="text-sm">{getStatusText(transfer.status)}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <span>{transfer.fromOwnerName}</span>
                    <ArrowRightIcon className="w-4 h-4" />
                    <span>{transfer.toOwnerName}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      {new Date(transfer.timestamp).toLocaleString()}
                    </span>
                    <span className="font-medium text-green-600">
                      {transfer.transferPrice} {transfer.currency}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 所有权历史 */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold">所有权历史</h2>
          </div>
          
          <div className="p-6">
            {ownershipHistory.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                暂无所有权记录
              </div>
            ) : (
              <div className="space-y-4">
                {ownershipHistory.map((ownership) => (
                  <div key={ownership.workId} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium">{ownership.workTitle}</h3>
                      <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded">
                        {ownership.ownershipPercentage}% 所有权
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <UserIcon className="w-4 h-4" />
                      <span>当前所有者: {ownership.currentOwnerName}</span>
                    </div>
                    
                    <div className="text-xs text-gray-500">
                      创建时间: {new Date(ownership.createdAt).toLocaleString()}
                    </div>
                    
                    <div className="mt-3 text-sm">
                      <span className="text-gray-500">转让次数: </span>
                      <span className="font-medium">{ownership.transfers.length}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 转让详情模态框 */}
      {selectedTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">转让详情</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">作品名称</label>
                  <p className="text-lg font-medium">{selectedTransfer.workTitle}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">转让类型</label>
                  <p className="text-lg">{getTransferTypeText(selectedTransfer.transferType)}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">转让方</label>
                  <p>{selectedTransfer.fromOwnerName}</p>
                  <p className="text-xs text-gray-400 font-mono">{selectedTransfer.fromOwner}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">受让方</label>
                  <p>{selectedTransfer.toOwnerName}</p>
                  <p className="text-xs text-gray-400 font-mono">{selectedTransfer.toOwner}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">转让价格</label>
                  <p className="text-lg font-bold text-green-600">
                    {selectedTransfer.transferPrice} {selectedTransfer.currency}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">转让比例</label>
                  <p className="text-lg">{selectedTransfer.transferPercentage}%</p>
                </div>
              </div>
              
              {selectedTransfer.expiryDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">授权到期时间</label>
                  <p>{new Date(selectedTransfer.expiryDate).toLocaleString()}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-500">转让条款</label>
                <p className="text-sm bg-gray-50 p-3 rounded">{selectedTransfer.terms}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">合约地址</label>
                  <p className="text-sm font-mono">{selectedTransfer.contractAddress}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">交易哈希</label>
                  <p className="text-sm font-mono">{selectedTransfer.transactionHash}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  {getStatusIcon(selectedTransfer.status)}
                  <span>状态: {getStatusText(selectedTransfer.status)}</span>
                </div>
                <span className="text-sm text-gray-500">
                  {new Date(selectedTransfer.timestamp).toLocaleString()}
                </span>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setSelectedTransfer(null)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 创建转让模态框 */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">发起版权转让</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">作品名称</label>
                <input
                  type="text"
                  value={transferForm.workTitle}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, workTitle: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="输入作品名称"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">受让方地址</label>
                <input
                  type="text"
                  value={transferForm.toOwner}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, toOwner: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0x..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">受让方名称</label>
                <input
                  type="text"
                  value={transferForm.toOwnerName}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, toOwnerName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="受让方名称"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">转让价格</label>
                  <input
                    type="number"
                    value={transferForm.transferPrice}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, transferPrice: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.0"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">货币类型</label>
                  <select
                    value={transferForm.currency}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, currency: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="SOL">SOL</option>
                    <option value="USDC">USDC</option>
                    <option value="IBOM">IBOM</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">转让类型</label>
                <select
                  value={transferForm.transferType}
                  onChange={(e) => {
                    const type = e.target.value as 'full' | 'partial' | 'license';
                    setTransferForm(prev => ({ 
                      ...prev, 
                      transferType: type,
                      transferPercentage: type === 'full' ? 100 : type === 'license' ? 0 : prev.transferPercentage
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="full">完全转让</option>
                  <option value="partial">部分转让</option>
                  <option value="license">授权许可</option>
                </select>
              </div>
              
              {transferForm.transferType === 'partial' && (
                <div>
                  <label className="block text-sm font-medium mb-2">转让比例 (%)</label>
                  <input
                    type="number"
                    value={transferForm.transferPercentage}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, transferPercentage: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="100"
                  />
                </div>
              )}
              
              {transferForm.transferType === 'license' && (
                <div>
                  <label className="block text-sm font-medium mb-2">授权到期时间</label>
                  <input
                    type="datetime-local"
                    value={transferForm.expiryDate}
                    onChange={(e) => setTransferForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">转让条款</label>
                <textarea
                  value={transferForm.terms}
                  onChange={(e) => setTransferForm(prev => ({ ...prev, terms: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="描述转让的具体条款和条件"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreateTransfer}
                className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
              >
                提交转让申请
              </button>
              <button
                onClick={() => setShowTransferModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CopyrightTransferManager;
